import type { ComponentPropsWithoutRef, ForwardedRef, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { background, color, compose, layout, position, space, typography } from 'styled-system';
import { theme } from 'styled-tools';

import LoadingIndicator from '@ui/buttons/ButtonLoadingIndicator.tsx';
import Icon from '@ui/icons/Icon.tsx';

import { useCSSColors } from '../../hooks/useCSSColors.ts';

type MapNavButtonStyleProps = {
  $borderTopRightRadius?: string;
  $borderTopLeftRadius?: string;
  $borderBottomLeftRadius?: string;
  $borderBottomRightRadius?: string;
  $boxShadow?: string;
};

type MapNavButtonProps = ComponentPropsWithoutRef<'button'> &
  MapNavButtonStyleProps & {
    iconName: string;
    degreesOfIconRotation?: number;
    loader?: boolean;
    boxShadow?: string;
    borderRadius?: string;
    'data-testid'?: string;
    children?: ReactNode;
    ariaLabel?: string;
  };

const StyledMapNavButton = styled('button')<MapNavButtonStyleProps>`
  background-color: ${theme('colors.mapNavBackground')};
  opacity: 0.85;
  width: 30px;
  height: 30px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: ${theme('radii.lg')};
  border-top-right-radius: ${({ $borderTopRightRadius }) => $borderTopRightRadius};
  border-top-left-radius: ${({ $borderTopLeftRadius }) => $borderTopLeftRadius};
  border-bottom-left-radius: ${({ $borderBottomLeftRadius }) => $borderBottomLeftRadius};
  border-bottom-right-radius: ${({ $borderBottomRightRadius }) => $borderBottomRightRadius};
  box-shadow: ${({ $boxShadow }) => $boxShadow};

  &:hover {
    opacity: 1;
  }
  &:active,
  &:focus {
    outline: none;
    border: none;
  }
  &:focus-visible {
    outline: var(--focus-outline);
    outline-offset: 2px;
    opacity: 1;
  }

  ${compose(background, color, layout, position, space, typography)}
`;

type MapNavDropShadowProps = {
  $boxShadow?: string;
  $borderRadius?: string;
  $borderTopLeftRadius?: string;
  $borderTopRightRadius?: string;
  $borderBottomLeftRadius?: string;
  $borderBottomRightRadius?: string;
};

const StyledMapNavButtonDropShadow = styled.div<MapNavDropShadowProps>`
  box-shadow: ${({ theme: currentTheme, $boxShadow }) => $boxShadow ?? currentTheme.shadows?.md};
  width: 30px;
  height: 30px;
  background-color: transparent;
  border-radius: ${({ theme: currentTheme, $borderRadius }) => $borderRadius ?? currentTheme.radii?.lg};
  border-top-left-radius: ${({ $borderTopLeftRadius }) => $borderTopLeftRadius};
  border-top-right-radius: ${({ $borderTopRightRadius }) => $borderTopRightRadius};
  border-bottom-left-radius: ${({ $borderBottomLeftRadius }) => $borderBottomLeftRadius};
  border-bottom-right-radius: ${({ $borderBottomRightRadius }) => $borderBottomRightRadius};

  &:focus-visible {
    outline: none;
    border: none;
  }

  & :has(:focus-visible) {
    outline: 2px solid darkorange;
    outline-offset: 1px;
  }
`;

const MapNavButton = forwardRef(function MapNavButton(
  {
    iconName,
    degreesOfIconRotation,
    ariaLabel: ariaLabelProp,
    'data-testid': dataTestId = 'MapNavButton',
    loader,
    boxShadow,
    borderRadius,
    $boxShadow,
    $borderTopLeftRadius,
    $borderTopRightRadius,
    $borderBottomLeftRadius,
    $borderBottomRightRadius,
    children,
    ...buttonProps
  }: MapNavButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const colors = useCSSColors() as Record<string, string>;
  const ariaLabel = ariaLabelProp || buttonProps['aria-label'] || (typeof children === 'string' ? children : '') || '';

  if (loader) {
    return (
      <StyledMapNavButton
        data-testid={dataTestId}
        $boxShadow={boxShadow ?? $boxShadow}
        $borderTopLeftRadius={$borderTopLeftRadius}
        $borderTopRightRadius={$borderTopRightRadius}
        $borderBottomLeftRadius={$borderBottomLeftRadius}
        $borderBottomRightRadius={$borderBottomRightRadius}
        ref={ref}
        aria-label={ariaLabel}
        {...buttonProps}
      >
        <LoadingIndicator spinnerColor="white" size="xxsm" />
      </StyledMapNavButton>
    );
  }

  return (
    <StyledMapNavButtonDropShadow
      $boxShadow={boxShadow ?? $boxShadow}
      $borderRadius={borderRadius}
      $borderTopLeftRadius={$borderTopLeftRadius}
      $borderTopRightRadius={$borderTopRightRadius}
      $borderBottomLeftRadius={$borderBottomLeftRadius}
      $borderBottomRightRadius={$borderBottomRightRadius}
    >
      <StyledMapNavButton
        data-testid={dataTestId}
        $boxShadow={boxShadow ?? $boxShadow}
        $borderTopLeftRadius={$borderTopLeftRadius}
        $borderTopRightRadius={$borderTopRightRadius}
        $borderBottomLeftRadius={$borderBottomLeftRadius}
        $borderBottomRightRadius={$borderBottomRightRadius}
        ref={ref}
        aria-label={ariaLabel}
        {...buttonProps}
      >
        <Icon
          name={iconName}
          iconSize="sm"
          degreesOfIconRotation={degreesOfIconRotation}
          color={colors.mapNavText}
          aria-hidden="true"
          focusable="false"
          wrapper="span"
        />
      </StyledMapNavButton>
    </StyledMapNavButtonDropShadow>
  );
});

export default MapNavButton;
